import { useCallback } from "react";

const CATALOG_CACHE_KEY = "triolo_offline_catalog";
const CATALOG_TTL_MS = 7 * 24 * 3600 * 1000; // 7 giorni

/**
 * Hook che gestisce la cache offline del catalogo prodotti per le pagine Giri.
 *
 * - `refresh()` → scarica l'intero catalogo (5000 prodotti) e lo salva in
 *   localStorage. Chiamato dopo il warming del SW cache.
 * - `search(q)` → ricerca client-side case-insensitive nel catalogo cached.
 *   Restituisce [] se la cache è scaduta (>7gg) o assente.
 */
export function useOfflineCatalogCache(authAxios) {
  const refresh = useCallback(async () => {
    try {
      const res = await authAxios.get(`/products?per_page=5000`);
      const products = Array.isArray(res.data) ? res.data : (res.data?.items || []);
      // Manteniamo solo i campi necessari per ridurre il peso in localStorage
      const lite = products.map((p) => ({
        id: p.id,
        sku: p.sku,
        name: p.name,
        category_name: p.category_name,
        macro_category: p.macro_category,
        unit: p.unit,
        iva_percentage: p.iva_percentage,
        price_list: p.price_list,
        price_min: p.price_min,
        price_auth: p.price_auth,
        price_promo: p.price_promo,
        non_disponibile: p.non_disponibile,
      }));
      localStorage.setItem(CATALOG_CACHE_KEY, JSON.stringify({ ts: Date.now(), products: lite }));
      console.log(`[OfflineCatalog] cached for offline: ${lite.length} products`);
    } catch (err) {
      console.debug("[OfflineCatalog] refresh failed:", err?.message || err);
    }
  }, [authAxios]);

  const getCached = useCallback(() => {
    try {
      const raw = localStorage.getItem(CATALOG_CACHE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (Date.now() - (parsed.ts || 0) > CATALOG_TTL_MS) return [];
      return parsed.products || [];
    } catch {
      return [];
    }
  }, []);

  const search = useCallback((q) => {
    const cat = getCached();
    if (!cat.length) return [];
    const needle = (q || "").toLowerCase().trim();
    if (!needle) return [];
    return cat.filter((p) =>
      (p.name || "").toLowerCase().includes(needle) ||
      (p.sku || "").toLowerCase().includes(needle)
    );
  }, [getCached]);

  return { refresh, search };
}
